import { create } from "zustand";

import { logger } from "@/telemetry";

/**
 * UI Preferences Store
 *
 * Manages user interface preferences with localStorage persistence.
 * Provides a single source of truth for UI toggles across the application.
 */

/**
 * Export font options for Word document exports.
 * "brand" uses the current brand's font family (Poppins for WebuildAI, Raleway for InvestIndustrial)
 */
export type ExportFontOption = "brand" | "Poppins" | "Calibri" | "Arial" | "Times New Roman";

/**
 * Font option configuration with system-safe metadata.
 * isSystemSafe indicates whether the font is pre-installed on most computers.
 *
 * IMPORTANT: Bundled fonts (Poppins, Raleway) are NOT system-safe for exports.
 * They only work in the web app via @font-face. DOCX files don't embed fonts,
 * so recipients without these fonts installed will see a substituted font.
 */
export const EXPORT_FONT_OPTIONS: {
  value: ExportFontOption;
  label: string;
  isSystemSafe: boolean;
}[] = [
  { value: "brand", label: "Use Brand Font", isSystemSafe: false }, // Poppins/Raleway - NOT pre-installed
  { value: "Poppins", label: "Poppins", isSystemSafe: false }, // NOT pre-installed on most systems
  { value: "Calibri", label: "Calibri", isSystemSafe: true }, // Pre-installed on Windows, macOS
  { value: "Arial", label: "Arial", isSystemSafe: true }, // Pre-installed on all systems
  { value: "Times New Roman", label: "Times New Roman", isSystemSafe: true }, // Pre-installed on all systems
];

/**
 * Page size options for Word document exports.
 * A4 is the default (standard international size).
 * Labels and dimensions use i18n translation keys (pageSize.*).
 */
export type ExportPageSize = "A4" | "Letter" | "Legal";

export const EXPORT_PAGE_SIZE_OPTIONS: {
  value: ExportPageSize;
  labelKey: string;
  dimensionsKey: string;
}[] = [
  { value: "A4", labelKey: "pageSize.A4", dimensionsKey: "pageSize.A4.dimensions" },
  { value: "Letter", labelKey: "pageSize.Letter", dimensionsKey: "pageSize.Letter.dimensions" },
  { value: "Legal", labelKey: "pageSize.Legal", dimensionsKey: "pageSize.Legal.dimensions" },
];

export interface UIPreferencesState {
  // Line numbers display preference for code artefacts
  showLineNumbers: boolean;

  // Export font preference for Word documents
  exportFontFamily: ExportFontOption;

  // Export page size preference for Word documents
  exportPageSize: ExportPageSize;

  // Show advanced download options (PNG, CSV, Markdown, HTML, Text)
  showAdvancedDownloadOptions: boolean;

  // Actions
  toggleLineNumbers: () => void;
  setExportFontFamily: (font: ExportFontOption) => void;
  setExportPageSize: (size: ExportPageSize) => void;
  toggleAdvancedDownloadOptions: () => void;
}

export const useUIPreferences = create<UIPreferencesState>((set, get) => ({
  // Initialize from localStorage
  showLineNumbers: (() => {
    try {
      const stored = localStorage.getItem("artefact-show-line-numbers");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Initialize export font from localStorage (default to "brand")
  exportFontFamily: (() => {
    try {
      const stored = localStorage.getItem("export-font-family");

      if (stored) {
        const parsed = JSON.parse(stored);

        // Validate that the stored value is a valid option
        const validOptions: ExportFontOption[] = [
          "brand",
          "Poppins",
          "Calibri",
          "Arial",
          "Times New Roman",
        ];

        if (validOptions.includes(parsed)) {
          return parsed as ExportFontOption;
        }
      }

      return "brand";
    } catch {
      return "brand";
    }
  })(),

  // Initialize export page size from localStorage (default to "A4")
  exportPageSize: (() => {
    try {
      const stored = localStorage.getItem("export-page-size");

      if (stored) {
        const parsed = JSON.parse(stored);

        // Validate that the stored value is a valid option
        const validOptions: ExportPageSize[] = ["A4", "Letter", "Legal"];

        if (validOptions.includes(parsed)) {
          return parsed as ExportPageSize;
        }
      }

      return "A4";
    } catch {
      return "A4";
    }
  })(),

  // Initialize advanced download options from localStorage (default to false)
  showAdvancedDownloadOptions: (() => {
    try {
      const stored = localStorage.getItem("show-advanced-download-options");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Toggle line numbers and persist to localStorage
  toggleLineNumbers: () => {
    const newValue = !get().showLineNumbers;

    set({ showLineNumbers: newValue });

    try {
      localStorage.setItem("artefact-show-line-numbers", JSON.stringify(newValue));
    } catch (error) {
      logger.warn("Failed to save line numbers preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set export font family and persist to localStorage
  setExportFontFamily: (font: ExportFontOption) => {
    set({ exportFontFamily: font });

    try {
      localStorage.setItem("export-font-family", JSON.stringify(font));
    } catch (error) {
      logger.warn("Failed to save export font preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set export page size and persist to localStorage
  setExportPageSize: (size: ExportPageSize) => {
    set({ exportPageSize: size });

    try {
      localStorage.setItem("export-page-size", JSON.stringify(size));
    } catch (error) {
      logger.warn("Failed to save export page size preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Toggle advanced download options and persist to localStorage
  toggleAdvancedDownloadOptions: () => {
    const newValue = !get().showAdvancedDownloadOptions;

    set({ showAdvancedDownloadOptions: newValue });

    try {
      localStorage.setItem("show-advanced-download-options", JSON.stringify(newValue));
    } catch (error) {
      logger.warn("Failed to save advanced download options preference to localStorage", {
        error: String(error),
      });
    }
  },
}));
